import {useCallback, useEffect, useMemo, useState} from "react";
import {variantDataRetrieverMapCreateEditDialog} from "./dialogBoxDataRetriever";
import {variantRawDataProcessorMapCreateEditDialog} from "./dialogBoxDataProcessor";
import {extractSections, variantDialogBoxConfigMap} from "./dialogBoxConfigRetriever";
import {useForm} from "react-hook-form";
import {hasValue, isPlainDict, toBytes, toBytesMultiplier} from "../../utils/utils";
import moment from "moment";
import {Dialog, DialogContent, IconButton, Typography} from "@mui/material";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";
import {Close} from "@mui/icons-material";
import {Accordion, AccordionDetails, AccordionSummary, StyledButtonContained} from "../../utils/uiUtils";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import {DataField} from "./DataField";


/**
 * @typedef CreateEditFieldOptions
 * @property {string | Array[]} source - Used in multiselect. Indicates selection options. Can be:
 * - string - property name that holds a list of values to be used as options
 * - Array of dicts - the list of values to be used as options. This should be accompanied by the "key" used for displaying and "key" used for actual value
 * - Array of values - the list of values to be used as options. The values will be used for both displaying and field value.
 * @property {string} displayKey - Used in multiselect. Used together with a source. Indicates the "key" to retrieve the display value.
 * @property {string} valueKey - Used in multiselect. Used together with a source. Indicates the "key" to retrieve the actual value.
 * @property {Array} safeValuesAlt - Used in multiselect. Some values like null or "" might be unsafe to be used as actual values of an option. This property can contain a list of dicts describing an alternative value to be used for these unsafe values. Each dict should contain 2 keys: valueToConvert and valueToConvertTo
 * @property {boolean} isSingleOption - Used in multiselect. If false, multiple selection is allowed. Default = true.
 * @property {string} unit - Used in sizeInput. The default unit in which the size will be represented and also saved at the end. Default = B. Can be:
 * - B Bytes
 * - KB 1000^1 B
 * - KiB 1024^1 B
 * - MB 1000^2 B
 * - MiB 1024^2 B
 * - GB 1000^3 B
 * - GiB 1024^3 B
 * - TB 1000^4 B
 * - TiB 1024^4 B
 * - PB 1000^5 B
 * - PiB 1024^5 B
 * @property {number} decimals - Number of decimals. If 0, number is integer, else float. Default = 0. Used in:
 * - sizeInput
 * - numberInput
 * @property {string | number = undefined} defValue - if provided, the field will try to default to this value if none set. Else will default to the "empty" one of the field type.
 * @property {string} dateFormat - Used in dateInput. Options:
 * - Standard accepted formats:
 *      - "iso"
 * - Actual date format like "YYYY-MM-DD"
 * @property {Object} validationRules - MUI / REACT compatible rules to be passed to the input field.
 * @property {boolean} requiredOnlyWhenCreating - if true, the required rule in validationRules applies while adding a record and is dropped while editing one. For a field the portal only started collecting recently: insisting on it when adding is reasonable, insisting on it when editing blocks every unrelated change to every record saved before it existed.
 * @property {function} optionValidator - Used in multiselect. A function that takes the option object as input and returns true if the option should be enabled, false otherwise.
 * @property {boolean} isControlled - dictates weather the inputField will be controlled by a Controller or uncontrolled (using ...register). Default = false
 * @property {boolean} isDisabled - if true, the field will be turned off. Default = false
 * @property {number} xs - The size of the field in xs grid system (12 based). Default = 12
 */

/**
 * @typedef CreateEditFieldConfig
 * @property {string} sectionName - The name of the section. All unique sections will be rendered as accordions. If empty string, no section will be created for this filed.
 * @property {string} label - The label of this field, used for display.
 * @property {string} valueProperty - The property that's set by this field. If property exists (edit mode), it'll be used as default.
 * @property {string} type - The type of field to be rendered. Options:
 * - text
 * - dialledNumberInput - a phone number as a country-code dropdown beside a national number box, stored as one string
 * - multiselect
 * - sizeInput
 * - numberInput
 * - dateInput
 * - toggleInput
 * @property {CreateEditFieldOptions} options - contains the options for field rendering
 */

/**
 *
 * @param props
 * @param {boolean} props.isDialogOpen - Whether the dialog is open
 * @param {function} props.handleClose - Function to close the dialog
 * @param {string} props.variant - The variant of the dialog box.
 * Depending on variant, automatic raw data retrieval and data processing might be implemented.
 * @param {Object = undefined} props.data - A dict type object that contains the data required for rendering.
 * If provided, this data will be used for rendering.
 * Otherwise, the RawData from rawDataRetriever.
 * @param {CreateEditFieldConfig[] = undefined} props.overridingConfig - An array of field configuration objects that'll enhance the default configuration for the given variant. This is intended as an addition that will override or add fields to the default configuration.
 * @param {string} props.title - The title of the dialog box.
 * @param {function} props.onSubmitFn - Function executed at submitting (pressing the Submit button or enter).
 * The entire enriched and processed data object will be passed to the function.
 * @constructor
 */
export const CreateEditDialog = (props) => {
    // Deconstruct props for easier access, every time props change, these will be updated
    const {
        isDialogOpen,
        handleClose,
        variant,
        data = undefined,
        overridingConfig = undefined,
        title,
        onSubmitFn
    } = props;

    const [rawData, setRawData] = useState({});
    const [processedData, setProcessedData] = useState({});
    const [loading, setLoading] = useState(false);
    /**
     * state designed to hold the configuration that'll drive the rendering of the dialog box fields
     */
    const [configuration, setConfiguration] = useState([]);
    const [sections, setSections] = useState([]);

    const [accordionsExpanded, setAccordionsExpanded] = useState({});


    const resetAccordions = () => {
        const accordionsLocalState = {};
        sections.forEach(sectionName => {
            accordionsLocalState[sectionName] = false;
        });
        setAccordionsExpanded(accordionsLocalState);
    };

    /**
     * When the data property or the variant of the dialog changes, reload into rawData the correct data. If data isn't provided, use the variant's data retriever for that.
     */
    useEffect(() => {
        if (isDialogOpen === false) return; // do nothing if the dialog isn't open

        if (isPlainDict(data)) setRawData(data)
        else {
            const dataRetrieverFn = variantDataRetrieverMapCreateEditDialog[variant] ?? variantDataRetrieverMapCreateEditDialog.defValue;
            dataRetrieverFn(setRawData, setLoading)
        }
    }, [variant, data, isDialogOpen]);

    /**
     * When an overriding configuration is provided, when it is changed or when the variant changes, update the configuration state that'll drive the rendering of the fields
     * At this moment, extract also the unique section names.
     */
    useEffect(() => {
        let config = (variantDialogBoxConfigMap[variant] ?? variantDialogBoxConfigMap.defValue)();
        if (overridingConfig && Array.isArray(overridingConfig)) {
            const overridingFieldNames = overridingConfig.map(fieldConfig => fieldConfig.valueProperty);
            const filteredOriginalConfig = config.filter(originalFieldConfig => !overridingFieldNames.includes(originalFieldConfig.valueProperty));

            config = [
                ...overridingConfig,
                ...filteredOriginalConfig
            ];
        }

        /*
         * A field marked requiredOnlyWhenCreating loses its required rule once the dialog is editing an
         * existing record. Mapped in place rather than merged through overridingConfig, because that path
         * prepends and the section order is taken from the order fields first appear in — an override would
         * silently move Contact Details above the name fields.
         */
        if (hasValue(data)) {
            config = config.map(fieldConfig => {
                if (!fieldConfig.options?.requiredOnlyWhenCreating) return fieldConfig;
                const {required, ...rulesWithoutRequired} = fieldConfig.options.validationRules ?? {};
                return {...fieldConfig, options: {...fieldConfig.options, validationRules: rulesWithoutRequired}};
            });
        }

        setConfiguration(config);
    }, [variant, overridingConfig, data])

    /**
     * When rawData changes, process it with the assigned variant processor. The processor can be a simple pass-through, but if data requires to be "massaged", here is the place to do it.
     * This is a good opportunity to convert any unsafe data as defined in configuration (check docstrings for options -> multiselect safeValuesAlt description)
     */
    useEffect(() => {
        const processorFn = variantRawDataProcessorMapCreateEditDialog[variant] ?? variantRawDataProcessorMapCreateEditDialog.defValue;
        processorFn(rawData, configuration, setProcessedData);
    }, [rawData, configuration]);

    useEffect(() => setSections(configuration ? extractSections(configuration) : []), [configuration]);

    const getDefaultValues = (_configuration, _processedData) => {
        const filterNameList = [];
        const defaultValues = {};

        _configuration.forEach(fieldConfig => {
            const fieldName = fieldConfig.valueProperty.replace(/\s+/g, "_").toLowerCase();
            if (filterNameList.includes(fieldName)) throw new Error(`CreateEditDialog: Configuration error. Duplicate field label / propertyName found: ${fieldName}`);
            filterNameList.push(fieldName);

            const fieldType = fieldConfig.type;

            /**
             * only applicable for sizeInput type and undefined otherwise. Value extracted from fieldConfig options or defaulted to "B"
             * @type {string | undefined}
             */
            let sizeUnitValue = (fieldType === "sizeInput") ? (fieldConfig?.options?.unit ?? "B") : undefined;

            /**
             * primary source of default value. If it's in edit mode, this will be the actual value of the field
             * @type {any | undefined}
             */
            let valueInData;
            valueInData = _processedData?.[fieldConfig.valueProperty];

            /**
             * fallback 1: default specified in the configuration
             */
            const valueInConfig = fieldConfig?.options?.defValue;

            /**
             * fallback 2: default "empty" value depending on field type
             */
            let defaultEmptyValue;
            switch (fieldType) {
                case "toggleInput":
                    defaultEmptyValue = false;
                    break;
                case "radioInput":
                    /*
                     * The first option, not "". A radio group with no value selected renders every button empty,
                     * which on a required field reads as a form that has not finished loading rather than one
                     * waiting for an answer. Configs are written with the safe choice first for that reason.
                     */
                    defaultEmptyValue = fieldConfig?.options?.source?.[0]?.value;
                    break;
                case "dateInput":
                    defaultEmptyValue = '';
                    break;
                case "multiselect":
                    defaultEmptyValue = [];
                    break;
                case "sizeInput":
                    defaultEmptyValue = "";
                    break;
                default:                       // text / int / float
                    defaultEmptyValue = "";
                    break;
            }

            /**
             * The order of precedence for default values is:
             * 1. value in data (edit mode)
             * 2. value in configuration (defValue)
             * 3. default empty value depending on field type
             */
            defaultValues[fieldName] = valueInData !== undefined ? valueInData : (valueInConfig !== undefined ? valueInConfig : defaultEmptyValue)
            /**
             * if sizeInput field type, add the unit field as well
             */
            if (sizeUnitValue) defaultValues[`${fieldName}__size_unit`] = sizeUnitValue;
        });

        return defaultValues;
    };

    /**
     * When the default values change (configuration or processedData change), defaults will be recalculated
     */
    const defaultValues = useMemo(
        () => getDefaultValues(configuration, processedData),
        [configuration, processedData]
    );

    const {
        register,
        handleSubmit,
        watch,
        formState: {errors, isValid, isDirty, isSubmitting, isValidating},
        setValue,
        control,
        getValues,
        reset,
        trigger
    } = useForm({
        defaultValues: {...defaultValues},
        mode: "onBlur",
        reValidateMode: "onChange",
    });

    /**
     * When resetForm changes (defaultValues change), reset the form with the new default values
     */
    const resetForm = useCallback(() => {
        reset(defaultValues);
        resetAccordions();
    }, [reset, defaultValues]);

    useEffect(() => {
        resetForm();
    }, [resetForm]);


    /**
     * Function executed at submitting.
     * It'll enrich the data with the missing properties, convert values to the expected format and then call the onSubmitFn provided in props
     * @param data - the data as is in the form, before any processing
     * @constructor
     */
    const SubmitFn = (data) => {
        const finalData = {};
        const defaults = getDefaultValues(configuration, processedData);

        configuration.forEach(fieldConfig => {
            const fieldName = fieldConfig.valueProperty.replace(/\s+/g, "_").toLowerCase();
            const fieldType = fieldConfig.type;
            const fieldValue = data[fieldName];
            const sizeUnitValue = ['sizeInput'].includes(fieldType) ? data[`${fieldName}__size_unit`] || 'B' : undefined;
            const _hasValue = hasValue(fieldValue);

            const convertAltValueToOriginal = (value) => {
                const safeValuesAlt = fieldConfig.options.safeValuesAlt
                if (!safeValuesAlt) return value;

                const alternative = safeValuesAlt.find(alt => alt.valueToConvertTo === value)
                if (alternative !== undefined) return alternative.valueToConvert;

                // field has a safer alternative list of values, but this one is not part of those
                return value;
            };

            let temp_val = undefined;

            switch (fieldType) {
                case "toggleInput":
                    finalData[fieldConfig.valueProperty] = fieldValue;
                    break;
                case "radioInput":
                    /*
                     * Passed through as it stands, like the toggle above, and deliberately without the
                     * `_hasValue ? fieldValue : defaults[fieldName]` guard every other branch uses. hasValue()
                     * has no boolean case and falls through to false, so that guard would discard the operator's
                     * answer and post the config default on every submit — including the one that matters, where
                     * they moved the radio off it. The default branch is no good either: it flattens `false` to
                     * null, and here `false` means a path was chosen rather than that none was.
                     */
                    finalData[fieldConfig.valueProperty] = fieldValue;
                    break;
                case "dateInput":
                    const date = !_hasValue ? defaults[fieldValue] : (fieldValue instanceof Date ? fieldValue : new Date(fieldValue));

                    if (date === "" || date === null || date === undefined) {
                        finalData[fieldConfig.valueProperty] = null;
                        break;
                    }

                    const format = fieldConfig?.options?.dateFormat ? fieldConfig?.options?.dateFormat : "iso";
                    switch (format) {
                        case "iso":
                            finalData[fieldConfig.valueProperty] = date.toISOString();
                            break;
                        default:
                            finalData[fieldConfig.valueProperty] = moment(date).format(format);
                    }
                    break;
                case "multiselect":
                    temp_val = _hasValue ? fieldValue : defaults[fieldName];

                    const isSingleOption = fieldConfig?.options?.isSingleOption ?? true;

                    if (isSingleOption) {
                        const tempSingleValue = Array.isArray(temp_val) ? temp_val[0] : temp_val;
                        finalData[fieldConfig.valueProperty] = convertAltValueToOriginal(tempSingleValue);
                    } else {
                        const tempArrayValue = Array.isArray(temp_val) ? temp_val : [temp_val];
                        finalData[fieldConfig.valueProperty] = tempArrayValue.map(val => convertAltValueToOriginal(val));
                    }
                    break;
                case "dialledNumberInput":
                    temp_val = _hasValue ? fieldValue : defaults[fieldName];

                    /*
                     * "" for an empty number, not the null the default branch below produces. The number is
                     * one CharField on the API and UserSerializer permits blank but not null, so a Phone left
                     * empty came back 400 "This field may not be null." — on a field this form deliberately
                     * marks optional. Add New User could not be completed at all without typing a phone
                     * number nobody asked for.
                     *
                     * Only this type is changed. The default branch feeds every other dialog in the portal,
                     * and its null is what several of them are built on.
                     */
                    if (temp_val === "" || temp_val === null || temp_val === undefined) {
                        finalData[fieldConfig.valueProperty] = "";
                        break;
                    }

                    finalData[fieldConfig.valueProperty] = temp_val;
                    break;
                case "sizeInput":
                    temp_val = _hasValue ? fieldValue : defaults[fieldName];

                    if (temp_val === "" || temp_val === null || temp_val === undefined) {
                        finalData[fieldConfig.valueProperty] = null;
                        break;
                    }

                    const reportUnit = fieldConfig?.options?.unit ?? "B";
                    finalData[fieldConfig.valueProperty] = toBytesMultiplier(toBytes(temp_val, sizeUnitValue), reportUnit);
                    break;
                default:                       // text / int / float
                    temp_val = _hasValue ? fieldValue : defaults[fieldName];

                    if (temp_val === "" || temp_val === null || temp_val === undefined) {
                        finalData[fieldConfig.valueProperty] = null;
                        break;
                    }

                    finalData[fieldConfig.valueProperty] = temp_val;
                    break;
            }

        });

        onSubmitFn(finalData);
    };

    /**
     * Function executed on dialog close (pressing the cancel button, clicking outside the dialog or pressing the escape key).
     * It'll reset the form and call the closeDialog function provided in props
     */
    const localHandleClose = () => {
        resetForm();
        handleClose();
    };

    const renderDialogTitle = () => {
        return (
            <DialogTitle id={`CreateEditDialog-${variant}-Title`} sx={{backgroundColor: '#3A3E3F', color: '#FFFFFF', display: "flex", justifyContent: 'space-between', alignItems: 'center'}}>
                <Typography sx={{color: '#FFC540', fontSize: 18}}>{title}</Typography>
                <IconButton onClick={localHandleClose} data-testid={`CreateEditDialog-${variant}-Close-Button`}> <Close sx={{color: '#FFFFFF'}}/> </IconButton>
            </DialogTitle>);
    }

    /**
     * Method renders all the fields that belong to a given section (sectionName). Used as helper in the later renderUnformattedSection or renderSection methods.
     * @param {string} sectionName - Empty string or section name
     * @return {React.JSX.Element} - Returns a Grid container with all the fields that belong to this section
     */
    const renderFieldsOfSection = (sectionName) => {
        return (
            <Grid container spacing={2}>
                {(configuration.filter(candidateConfig => candidateConfig.sectionName === sectionName) || []).map((fieldConfig, fieldIndex) => {
                    const fieldName = fieldConfig.valueProperty.replace(/\s+/g, "_").toLowerCase();

                    return (
                        <DataField
                            key={`DataField-${fieldIndex}-${fieldName}`}
                            data_testid={`DataField-${fieldName}`}
                            fieldConfig={fieldConfig}
                            processedData={processedData}
                            defaultValue={defaultValues[fieldName]}
                            register={register}
                            control={control}
                            getValues={getValues}
                            trigger={trigger}
                        />);
                })}
            </Grid>
        );
    };

    /**
     * Method renders all the fields that don't belong to any section (sectionName = "")
     * No accordion is created for these fields.
     */
    const renderUnformattedSection = () => {
        return renderFieldsOfSection("");
    };

    const ensureSectionOpen = (name) => setAccordionsExpanded(prev => (prev[name] ? prev : {...prev, [name]: true}));

    /**
     * Method renders a section with the given name.
     * An accordion is created for this section, and all fields belonging to this section are rendered inside the accordion.
     * @param {string} sectionName - The name of the section to be rendered
     */
    const renderSection = (sectionName) => {
        return (
            <Accordion
                expanded={accordionsExpanded[sectionName] ?? false}
                onChange={(_, isExpanded) => {
                    setAccordionsExpanded(prev => ({...prev, [sectionName]: isExpanded}));
                }}
                TransitionProps={{unmountOnExit: false}}
            >
                <AccordionSummary
                    aria-controls={`${sectionName}-content`}
                    id={`${sectionName}-header`}
                    onFocusVisible={() => setAccordionsExpanded(prev => prev[sectionName] ? prev : {...prev, [sectionName]: true})}
                >
                    <Typography sx={{fontFamily: "Poppins", fontSize: 12}}>{sectionName}</Typography>
                </AccordionSummary>
                <AccordionDetails onFocusCapture={() => ensureSectionOpen(sectionName)}>
                    {renderFieldsOfSection(sectionName)}
                </AccordionDetails>
            </Accordion>);
    };

    const renderDialogContent = () => {
        return (
            <DialogContent sx={{backgroundColor: '#3A3E3F', color: '#FFFFFF', paddingLeft: "24px", paddingRight: "24px", paddingTop: "16px", paddingBottom: "16px"}}>
                {loading ?
                    <Box
                        sx={{
                            flexGrow: 1,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            width: '100%',
                        }}>
                        <CircularProgress/>
                    </Box>
                    : <Grid container rowSpacing={1} alignItems="center" sx={{pt: 1}}>
                        {sections.map((sectionName, sectionIndex) => [
                                <Grid item xs={12} key={`section-${sectionIndex}-${sectionName}`}>
                                    {sectionName === "" ?
                                        renderUnformattedSection()
                                        : renderSection(sectionName)}
                                </Grid>
                            ]
                        )}
                    </Grid>
                }

            </DialogContent>);
    }

    const renderDialogActions = () => {
        return (
            <DialogActions sx={{backgroundColor: '#3A3E3F', color: '#FFFFFF', justifyContent: 'space-between', paddingLeft: "24px", paddingRight: "24px", paddingTop: "16px", paddingBottom: "16px"}}>
                <StyledButtonContained variant='contained'
                                       onClick={resetForm}
                                       sx={{width: '120px', height: '40px', cursor: 'pointer'}}>
                    Clear
                </StyledButtonContained>
                <StyledButtonContained variant="contained"
                                       type='submit'
                                       form={`CreateEditDialog-${variant}-form`}
                                       disabled={!isValid || isSubmitting || isValidating || !isDirty}
                                       sx={{width: '120px', height: '40px'}}>
                    Submit
                </StyledButtonContained>
            </DialogActions>);
    }

    return (
        <form id={`CreateEditDialog-${variant}-form`} onSubmit={handleSubmit(SubmitFn, errors => console.error(errors))}>
            <Dialog
                open={isDialogOpen}
                fullWidth={true}
                onClose={localHandleClose}
                scroll={"paper"}
                aria-labelledby={`CreateEditDialog-${variant}-Title`}
            >
                {renderDialogTitle()}
                {renderDialogContent()}
                {renderDialogActions()}
            </Dialog>
        </form>
    );
};